import React, { useState } from 'react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';

const benefits = [
  {
    id: 'instant-answers',
    title: 'Instant Answers, Anytime',
    description: 'No more bottlenecks. CLEONA provides 24/7 responses from your own resources, reducing repetitive questions and saving hours of staff time.',
    icon: '/images/benefits/icon-brain.png'
  },
  {
    id: 'clarity',
    title: 'Clarity from Complex Information',
    description: 'Turn dense policies, manuals, and FAQs into simple, conversational answers. Your audience gets exactly what they need without digging through documents.',
    icon: '/images/benefits/icon-info.png'
  },
  {
    id: 'team-support',
    title: 'Support for Your Team',
    description: 'Free up staff to focus on high-value work by letting CLEONA handle common questions. Better support without hiring more people.',
    icon: '/images/benefits/icon-support.png'
  },
  {
    id: 'learning',
    title: 'Guidance for Learning and Training',
    description: 'Help new team members and users find answers fast. CLEONA becomes an always-available training companion that knows your materials inside and out.',
    icon: '/images/benefits/icon-team.png'
  }
];

export default function BenefitsSection() {
  const [activeId, setActiveId] = useState('instant-answers');

  return (
    <section className="py-24 bg-white relative overflow-hidden">
      {/* Background gradient */}
      <div
        className="absolute pointer-events-none"
        style={{
          width: '2777px',
          height: '2424px',
          left: '-656px',
          top: '-722px',
          transform: 'rotate(-6.09deg)',
          background: 'linear-gradient(180deg, rgba(178, 237, 227, 0.3) 0%, rgba(255, 255, 255, 0) 50%)',
          borderRadius: '50%',
        }}
      />
      <div className="mx-auto max-w-7xl px-6 lg:px-8 relative z-10">
        {/* Section header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 rounded-pill bg-cleona-600 px-4 py-2 mb-4">
            <span className="text-white font-manrope font-semibold text-sm">Why CLEONA</span>
            <span className="flex items-center justify-center w-6 h-6 rounded-full bg-white">
              <svg className="w-3 h-3 text-cleona-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
              </svg>
            </span>
          </div>
          <h2 className="font-jakarta text-4xl font-bold text-gray-900 sm:text-5xl mb-4">
            How CLEONA Helps
          </h2>
          <p className="max-w-2xl mx-auto text-gray-600 font-manrope">
            CLEONA turns hard-to-find information into instant answers people can trust. It removes the frustration of searching, waiting, or asking staff for help.
          </p>
        </div>

        {/* Benefits grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          {/* Left - Accordion */}
          <div className="space-y-4">
            {benefits.map((benefit) => (
              <div
                key={benefit.id}
                className={`border rounded-2xl transition-all cursor-pointer ${
                  activeId === benefit.id
                    ? 'border-cleona-600 bg-cleona-50/50'
                    : 'border-gray-200 hover:border-cleona-300'
                }`}
                onClick={() => setActiveId(activeId === benefit.id ? null : benefit.id)}
              >
                <div className="p-6">
                  <div className="flex items-start gap-4">
                    <img
                      src={benefit.icon}
                      alt=""
                      className="w-12 h-12 flex-shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <h3 className="font-jakarta text-lg font-bold text-gray-900">
                          {benefit.title}
                        </h3>
                        <ChevronDownIcon
                          className={`w-5 h-5 text-cleona-600 transition-transform ${
                            activeId === benefit.id ? 'rotate-180' : ''
                          }`}
                        />
                      </div>
                      {activeId === benefit.id && (
                        <p className="mt-3 text-gray-600 font-manrope text-sm leading-relaxed">
                          {benefit.description}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Right - Laptop mockup */}
          <div className="relative flex items-center justify-center">
            <img
              src="/images/benefits/laptop-mockup.png"
              alt="CLEONA Dashboard"
              className="w-full max-w-lg h-auto"
            />
          </div>
        </div>
      </div>
    </section>
  );
}
